/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { filterDOMProps } from '@react-aria/utils';
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type HTMLAttributes, createContext, forwardRef } from 'react';
import { useNumberFormatter } from 'react-aria';
import { type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { Text, TextContext, type TextProps } from "../text/text.component";

interface NumberBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style' | 'className' | 'children'> {
  slot?: string;
  /** The size of the badge. (Default: 20) */
  size?: keyof typeof sizeVariants;
  /** The variant of the badge. (Default: neutral) */
  color?: keyof typeof fillColorVariants;
  /** Whether the badge is pastel. (Default: false) */
  isPastel?: boolean;
  /** The number to display. (Default: 0) */
  children?: string | number;
  style?: stylex.StyleXStyles;
}

const NumberBadgeContext = createContext<ContextValue<NumberBadgeProps, HTMLSpanElement>>({});

const NumberBadge = forwardRef((props: NumberBadgeProps, ref: ForwardedRef<HTMLSpanElement>) => {
  [props, ref] = useContextProps(props, ref, NumberBadgeContext);

  const {
    size = 20,
    color = 'neutral',
    isPastel = false,
    style,
    children = '0',
    ...otherProps
  } = props;

  const formatter = useNumberFormatter({
    style: 'decimal',
    minimumFractionDigits: 0,
    signDisplay: 'never',
  });

  return (
    <span
      {...filterDOMProps(otherProps)}
      role="presentation"
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        isPastel ? pastelColorVariants[color] : fillColorVariants[color],
        style
      )}>
      <Provider values={[
        [
          TextContext,
          {
            size: 12,
            color: textColors[isPastel ? 'pastel' : 'fill'][color],
          },
        ],
      ]}>
        <Text>{formatter.format(parseFloat(children.toString()))}</Text>
      </Provider>
    </span>
  );
});

const textColors: {
  fill: Record<keyof typeof fillColorVariants, TextProps['color']>,
  pastel: Record<keyof typeof pastelColorVariants, TextProps['color']>
} = {
  fill: {
    critical: 'white',
    attention: 'black',
    informational: 'white',
    neutral: 'white',
    disabled: 'buttonContentDisabled',
  },
  pastel: {
    critical: 'critical',
    attention: 'orange',
    informational: 'interactive',
    neutral: 'tertiary',
    disabled: 'buttonContentDisabled',
  },
};

const styles = stylex.create({
  base: {
    borderRadius: 999,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'inline-flex',
    justifyContent: 'center',
  },
});

const sizeVariants = stylex.create({
  16: {
    height: 16,
    minWidth: 16,
    paddingBottom: 0,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 0,
  },
  20: {
    height: 20,
    minWidth: 20,
    paddingBottom: 2,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2,
  },
  24: {
    height: 24,
    minWidth: 24,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
  },
});

const fillColorVariants = stylex.create({
  critical: { backgroundColor: $.surfaceCritical },
  attention: { backgroundColor: $.accentSun60 },
  informational: { backgroundColor: $.surfaceHighlight },
  neutral: { backgroundColor: $.opacity48 },
  disabled: { backgroundColor: $.buttonSurfaceDisabled },
});

const pastelColorVariants = stylex.create({
  critical: { backgroundColor: $.surfaceCriticalMedium },
  attention: { backgroundColor: $.surfaceAttentiveMedium },
  informational: { backgroundColor: $.surfaceHighlightMedium },
  neutral: { backgroundColor: $.opacity08 },
  disabled: { backgroundColor: $.buttonSurfaceDisabled },
});

export { NumberBadge, NumberBadgeContext, type NumberBadgeProps };
